import React, { useMemo } from 'react';
import { Outlet, useNavigate, useParams, NavLink } from 'react-router-dom';
import { getAllEvents } from '../services/api';
import { useApiQuery } from '../hooks/useApiQuery';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const EventDashboardLayout = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  // ✅ React Query: Fetch all events
 const {
  data: allEvents = [],
  isLoading: eventsLoading,
  error: eventsError,
} = useApiQuery(
  ['all-events'],
  async () => {
    const res = await getAllEvents();

    return Array.isArray(res.data)
      ? res.data
      : res.data?.data || [];
  },
  {
    staleTime: 5 * 60 * 1000,
    onError: () => {
      console.error('Failed to load events');
    },
  }
);

  // ✅ Find the current event
  const event = useMemo(() => {
    return allEvents.find(e => e._id === eventId);
  }, [allEvents, eventId]);

  // ✅ Navigation items
  const navItems = [
    { path: 'overview', label: 'Overview', icon: '📊' },
    { path: 'entry-exit', label: 'Entry/Exit', icon: '🔄' },
    { path: 'users', label: 'Users', icon: '👥' },
    { path: 'devices', label: 'Devices', icon: '📱' },
    { path: 'peak-hours', label: 'Peak Hours', icon: '🕒' }
  ];

  // ✅ Loading state
  if (eventsLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading event dashboard...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (eventsError) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <p className="text-red-500 text-lg">Failed to load event</p>
          <button
            onClick={() => navigate('/events-manager')}
            className="mt-4 text-[#1672b9] hover:underline"
          >
            Go back to Events
          </button>
        </div>
      </div>
    );
  }

  // ✅ No event found
  if (!event) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <p className="text-red-500 text-lg">Event not found</p>
          <button
            onClick={() => navigate('/events-manager')}
            className="mt-4 text-[#1672b9] hover:underline"
          >
            Go back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100 shadow-sm">
        <div className="px-6 py-4">
          {/* Top row: back button + title */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/events-manager')}
                className="p-2 rounded-md text-gray-500 hover:text-[#1672b9] hover:bg-gray-50 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Event Dashboard: {event?.name}
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Detailed analytics and monitoring
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex flex-wrap gap-1 mt-4 border-b border-gray-100">
            {navItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === 'overview'}
                className={({ isActive }) =>
                  `inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-white text-[#1672b9] border-b-2 border-[#1672b9] -mb-px'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`
                }
              >
                <span className="text-base">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      {/* Main content area – passes eventId and event via outlet context */}
      <div className="p-6">
        <Outlet context={{ eventId, event }} />
      </div>
    </div>
  );
};

export default EventDashboardLayout;